import PercentageIndicator from "@/components/portfolio/PercentageIndicator";
import { StatusPill } from "@/components/portfolio/StatusPill";
import { DismissibleSuggestion } from "@/components/ui/DismissibleSuggestion";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { PanelHeader } from "@/components/ui/PanelHeader";
import {
  SummaryMetricGrid,
  type SummaryTone,
} from "@/components/ui/SummaryGrid";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type RentComparisonView = "current" | "suggested" | "renovated";

export type RentComparisonOption = {
  id: RentComparisonView;
  label: string;
  rent: ReactNode;
  increase: ReactNode;
  increaseTone?: SummaryTone;
  percentage?: {
    valueNow: number;
    valueBefore: number;
  };
};

export type RentComparisonSuggestion = {
  key: string;
  label: string;
  dismissLabel: string;
  onDismiss: () => void;
};

type RentComparisonPanelProps = {
  title?: string;
  description?: string;
  valueLabel: string;
  currentValueLabel: string;
  currentValue: ReactNode;
  renovatedValueLabel: string;
  renovatedValue: ReactNode;
  valueIncreaseLabel: string;
  valueIncrease: ReactNode;
  valuePercentage?: {
    valueNow: number;
    valueBefore: number;
  };
  valueSuggestions?: readonly RentComparisonSuggestion[];
  rentSuggestions?: readonly RentComparisonSuggestion[];
  waitingForMarketData?: {
    label: string;
    detail: string;
  };
  options: readonly RentComparisonOption[];
  rentLabel: string;
  locale?: string;
  className?: string;
};

export function RentComparisonPanel({
  title,
  description,
  valueLabel,
  currentValueLabel,
  currentValue,
  renovatedValueLabel,
  renovatedValue,
  valueIncreaseLabel,
  valueIncrease,
  valuePercentage,
  valueSuggestions = [],
  rentSuggestions = [],
  waitingForMarketData,
  options,
  rentLabel,
  locale = "cs-CZ",
  className,
}: RentComparisonPanelProps) {
  const hasHeader = Boolean(title || description);

  return (
    <Panel as="section" variant="section" className={cn("w-full", className)}>
      {hasHeader ? (
        <PanelHeader title={title} description={description} />
      ) : null}

      {waitingForMarketData ? (
        <div className="app-renovation-recommendation">
          <StatusPill tone="neutral" appearance="flat">
            {waitingForMarketData.label}
          </StatusPill>
          <span>{waitingForMarketData.detail}</span>
        </div>
      ) : (
        <PanelGrid className="app-rent-comparison-groups">
          <div className="app-rent-comparison-group">
            <div className="app-rent-comparison-group-header">
              <p className="app-rent-comparison-group-title">{valueLabel}</p>
              <div className="flex flex-wrap justify-end gap-2">
                {valueSuggestions.map((suggestion) => (
                  <DismissibleSuggestion
                    key={suggestion.key}
                    dismissLabel={suggestion.dismissLabel}
                    onDismiss={suggestion.onDismiss}
                  >
                    {suggestion.label}
                  </DismissibleSuggestion>
                ))}
              </div>
            </div>
            <SummaryMetricGrid
              metrics={[
                {
                  label: currentValueLabel,
                  value: currentValue,
                },
                {
                  label: renovatedValueLabel,
                  value: renovatedValue,
                },
                {
                  label: valueIncreaseLabel,
                  value: (
                    <span className="app-rent-comparison-change">
                      <span>{valueIncrease}</span>
                      {valuePercentage && valuePercentage.valueBefore > 0 ? (
                        <PercentageIndicator
                          valueNow={valuePercentage.valueNow}
                          valueBefore={valuePercentage.valueBefore}
                          variant="banner"
                          locale={locale}
                        />
                      ) : null}
                    </span>
                  ),
                  tone: "positive",
                },
              ]}
            />
          </div>

          <div className="app-rent-comparison-group">
            <div className="app-rent-comparison-group-header">
              <p className="app-rent-comparison-group-title">{rentLabel}</p>
              <div className="flex flex-wrap justify-end gap-2">
                {rentSuggestions.map((suggestion) => (
                  <DismissibleSuggestion
                    key={suggestion.key}
                    dismissLabel={suggestion.dismissLabel}
                    onDismiss={suggestion.onDismiss}
                  >
                    {suggestion.label}
                  </DismissibleSuggestion>
                ))}
              </div>
            </div>
            <SummaryMetricGrid
              metrics={options.map((option) => ({
                label: option.label,
                value: (
                  <span className="app-rent-comparison-rent">
                    <span>{option.rent}</span>
                    {option.percentage &&
                    option.percentage.valueBefore > 0 ? (
                      <span className="app-rent-comparison-change">
                        <span>{option.increase}</span>
                        <PercentageIndicator
                          valueNow={option.percentage.valueNow}
                          valueBefore={option.percentage.valueBefore}
                          variant="banner"
                          locale={locale}
                        />
                      </span>
                    ) : null}
                  </span>
                ),
                tone: option.increaseTone,
              }))}
            />
          </div>
        </PanelGrid>
      )}
    </Panel>
  );
}
